import { useState, type FormEvent } from 'react'
import type { RankingDecision } from '../index.js'
import type { WorkerTableRow } from '../../roster/index.js'
import { ADMIN_API_BASE_URL, EndpointHint } from '../../core/index.js'
import { requestRanking, type PartnerSession } from '../lib/partnerApi.js'
import { RankingResult } from '../components/RankingList.js'
import { Play } from 'lucide-react'
import { ErrorNote, INPUT_CLASS, Labeled, describeError } from './formControls.js'
import { Button } from '../../core/index.js'

// Mirrors the API's limit, so an oversized pool is caught before the request.
const MAX_POOL_SIZE = 500

/** Ids typed one per line or comma-separated, trimmed, with blanks dropped. */
function parseIds(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((id) => id.trim())
    .filter((id) => id !== '')
}

interface RankingFormProps {
  session: PartnerSession
  facilityId: string
  /** The facility's roster, in the order the partner will "send" it. */
  workers: WorkerTableRow[]
}

/**
 * Sends `POST /facilities/{facilityId}/rankings` as the partner: a pool the
 * partner has already qualified, returned in reliability order. The pool is
 * built from the roster in name order, which is deliberately not reliability
 * order, so the reordering is visible.
 */
export function RankingForm({ session, facilityId, workers }: RankingFormProps) {
  const [shiftRef, setShiftRef] = useState('')
  const [selected, setSelected] = useState<readonly string[]>([])
  const [extraIds, setExtraIds] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [decision, setDecision] = useState<RankingDecision | null>(null)

  // Roster picks first, in roster order, then anything typed in - de-duplicated, since the API rejects a repeated id.
  const pool = [...new Set([...workers.filter((worker) => selected.includes(worker.workerId)).map((worker) => worker.workerId), ...parseIds(extraIds)])]
  const isPoolTooLarge = pool.length > MAX_POOL_SIZE
  const canSend = shiftRef.trim() !== '' && pool.length > 0 && !isPoolTooLarge && !isSending
  const allSelected = workers.length > 0 && selected.length === workers.length

  function toggle(workerId: string): void {
    setSelected((current) => (current.includes(workerId) ? current.filter((id) => id !== workerId) : [...current, workerId]))
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!canSend || !ADMIN_API_BASE_URL) {
      return
    }
    setIsSending(true)
    setError(null)
    setDecision(null)
    try {
      setDecision(await requestRanking(ADMIN_API_BASE_URL, session, facilityId, { shift_ref: shiftRef.trim(), worker_ids: pool }))
    } catch (err) {
      setError(describeError(err, 'The ranking request failed.'))
    } finally {
      setIsSending(false)
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
      <Labeled label="Shift reference" htmlFor="ranking-shift-ref" hint="The partner's own reference for the shift being filled. Stored with the ranking; not interpreted.">
        <input id="ranking-shift-ref" className={`${INPUT_CLASS} font-mono`} value={shiftRef} onChange={(event) => setShiftRef(event.target.value)} />
      </Labeled>

      <fieldset>
        <legend className="flex items-center gap-3 text-xs font-bold text-ink-mid mb-1">
          Qualified pool
          {workers.length > 0 && (
            <button
              type="button"
              onClick={() => setSelected(allSelected ? [] : workers.map((worker) => worker.workerId))}
              className="text-[11px] font-bold text-marine hover:text-marine-dk"
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          )}
        </legend>
        {workers.length === 0 ? (
          <p className="text-[11px] text-ink-mid">This facility has no workers on its roster yet.</p>
        ) : (
          <div className="border border-marine/15 max-h-56 overflow-auto divide-y divide-marine/10">
            {workers.map((worker) => (
              <label key={worker.workerId} className="flex items-center gap-3 px-3 py-1.5 text-xs text-ink cursor-pointer hover:bg-canvas">
                <input type="checkbox" checked={selected.includes(worker.workerId)} onChange={() => toggle(worker.workerId)} />
                <span className="font-bold flex-1">{worker.name}</span>
                <span className="font-mono text-[10px] text-ink-mid">{worker.workerId}</span>
              </label>
            ))}
          </div>
        )}
        <p className="text-[11px] text-ink-mid mt-1">Sent in the order shown (by name), which the partner would have chosen for its own reasons.</p>
      </fieldset>

      <Labeled
        label="Other worker IDs"
        htmlFor="ranking-extra-ids"
        hint="Optional, one per line. Use this to see how a worker the facility has no record of is handled."
      >
        <textarea
          id="ranking-extra-ids"
          rows={2}
          className={`${INPUT_CLASS} font-mono`}
          value={extraIds}
          onChange={(event) => setExtraIds(event.target.value)}
        />
      </Labeled>

      <Button icon={Play} size="lg" type="submit" disabled={!canSend}>
        {isSending ? 'Ranking…' : `Rank ${pool.length} ${pool.length === 1 ? 'worker' : 'workers'}`}
      </Button>
      <EndpointHint className="ml-3">POST /facilities/{facilityId}/rankings</EndpointHint>
      {isPoolTooLarge && <p className="text-[11px] font-bold text-coral">A pool can hold at most {MAX_POOL_SIZE} workers.</p>}

      {error && <ErrorNote message={error} />}
      {decision && (
        <div className="border border-marine/15 bg-canvas p-4">
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-2">
            Ranking <span className="font-mono normal-case tracking-normal">{decision.rankingId}</span>
          </div>
          <RankingResult decision={decision} names={new Map(workers.map((worker) => [worker.workerId, worker.name]))} />
        </div>
      )}
    </form>
  )
}
